/**
 * W3 动作选用 → 镜级 references[role=motion] 的单点写入。
 *
 * - assetRef = 库素材 objectKey(ai 编译层裸 key 现签;backend 不提前 presign);
 * - durationSec 必随行(ai 编译层 15.2s 墙的数据源;缺 = 裸奔到 Ark 400);
 * - job 必写(ai 铁律:无 job 的参考拒进槽)—— 默认派活句 + 素材名,用户可感知;
 * - 每镜至多 1 条 motion(ai 配额同源):再选 = 替换,其余 role(导演派的 identity/product)不动。
 */
import type { VideoShotReference } from "../api/videoService";
import type { PanelShotData } from "./panelShot";

export interface MotionPickSource {
  name: string;
  objectKey: string;
  durationSec: number | null;
}

/** 选中动作 → 新 PanelShotData(替换既有 motion 参考,保留其他 role)。 */
export function applyMotionPick(shot: PanelShotData, asset: MotionPickSource): PanelShotData {
  const others = (shot.references ?? []).filter((r) => r.role !== "motion");
  const picked: VideoShotReference = {
    assetRef: asset.objectKey,
    role: "motion",
    job: `本镜主体照「${asset.name}」的动作与节奏表演`,
    ...(asset.durationSec != null ? { durationSec: asset.durationSec } : {}),
  };
  return { ...shot, references: [...others, picked] };
}

/** 清除本镜动作参考(其他 role 不动;全空落 undefined,shotList optional 列语义)。 */
export function clearMotionPick(shot: PanelShotData): PanelShotData {
  const others = (shot.references ?? []).filter((r) => r.role !== "motion");
  return { ...shot, references: others.length > 0 ? others : [] };
}

/** 本镜已选的 motion 参考(至多一条)。 */
export function pickedMotion(shot: PanelShotData): VideoShotReference | undefined {
  return (shot.references ?? []).find((r) => r.role === "motion");
}

/** 已选动作的展示名:job 派活句里的「素材名」;解析不出退 objectKey 文件名。 */
export function pickedMotionLabel(ref: VideoShotReference | undefined): string | null {
  if (!ref) return null;
  const m = /「(.+?)」/.exec(ref.job ?? "");
  if (m) return m[1];
  const file = ref.assetRef.split("/").pop() ?? ref.assetRef;
  return file || null;
}

/**
 * 2026-08-04 导演自动配动作:编排回执里的 motion 派活 → shotIdx→assetRef 映射。
 * hydrate/applyBootstrap 时记一份,渲染期与当前镜比对(isAutoMotion)——
 * assetRef 没被用户换过才标「AI 配」,换/清后标注自然失效,零事件拦截。
 */
export function autoMotionByShot(
  references: VideoShotReference[][] | undefined
): Record<number, string> {
  const out: Record<number, string> = {};
  (references ?? []).forEach((refs, i) => {
    const m = (refs ?? []).find((r) => r.role === "motion");
    if (m) out[i] = m.assetRef;
  });
  return out;
}

/** 该镜当前动作是否仍是编排时 AI 配的那条(标注判据)。 */
export function isAutoMotion(
  auto: Record<number, string>,
  index: number,
  shot: PanelShotData
): boolean {
  const m = pickedMotion(shot);
  return !!m && auto[index] === m.assetRef;
}

/** 逐镜参考摘要(确认展示:「人物图×2 + 动作×1」;空返 null —— 概览不许比实际乐观)。 */
export function referenceSummary(refs: VideoShotReference[] | undefined): string | null {
  if (!refs || refs.length === 0) return null;
  const label: Record<string, string> = {
    identity: "人物图",
    product: "产品图",
    motion: "动作",
  };
  const counts = new Map<string, number>();
  for (const r of refs) {
    const k = label[r.role] ?? r.role;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return [...counts.entries()].map(([k, n]) => `${k}×${n}`).join(" + ");
}
